import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Package, ShieldCheck, Lock, ShoppingCart, ImageOff, Star,
  Tag, Boxes, BadgeCheck, Truck, ClipboardList, Eye, Clock, AlertCircle,
} from 'lucide-react';
import AppHeader from '@/components/AppHeader';
import AppFooter from '@/components/AppFooter';
import StatusBadge from '@/components/ui/StatusBadge';
import OrderModal from '@/components/OrderModal';
import { formatINR } from '@/lib/format';
import { listingsApi } from '@/lib/api';
import { getStoredUser, clearAuth } from '@/lib/authStore';
import { addRecentlyViewed, getRecentlyViewedExcluding } from '@/lib/recentlyViewed';
import { Toaster, toast } from 'sonner';

// ─── Small product-card used for Recommended / Recently Viewed rails ──────
function MiniProductCard({ listing, isGuest }) {
  return (
    <Link
      to={`/marketplace/${listing.id}`}
      className="listing-card flex flex-col h-full flex-shrink-0"
      style={{ width: '220px' }}
    >
      <div className="relative w-full overflow-hidden" style={{ height: '110px', backgroundColor: 'var(--background)' }}>
        {listing.isFeatured && (
          <div
            className="absolute top-0 left-0 z-10 flex items-center gap-1 px-1.5 py-0.5 text-xs font-heading tracking-stamp"
            style={{ backgroundColor: 'var(--amber)', color: 'white', fontFamily: 'var(--font-heading)', fontSize: '10px' }}
          >
            <Star size={9} fill="white" />
            FEATURED
          </div>
        )}
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageOff size={18} style={{ color: 'var(--muted-foreground)' }} />
          </div>
        )}
      </div>
      <div className="p-2.5 flex flex-col flex-1">
        <p className="text-xs font-medium leading-snug line-clamp-2 mb-1.5" style={{ minHeight: '2rem' }}>
          {listing.title}
        </p>
        {isGuest ? (
          <p className="text-xs font-heading" style={{ fontFamily: 'var(--font-mono)', filter: 'blur(4px)', color: 'var(--primary)' }}>
            {formatINR(listing.price)}
          </p>
        ) : (
          <p className="text-xs font-heading" style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary)' }}>
            {formatINR(listing.price)}
          </p>
        )}
        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--muted-foreground)' }}>{listing.sellerCity}</p>
      </div>
    </Link>
  );
}

function ProductRail({ title, icon: Icon, listings, isGuest, emptyMessage }) {
  if (!listings || listings.length === 0) {
    return emptyMessage ? (
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Icon size={16} style={{ color: 'var(--primary)' }} />
          <h2 className="font-heading text-lg tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h2>
        </div>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{emptyMessage}</p>
      </div>
    ) : null;
  }

  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} style={{ color: 'var(--primary)' }} />
        <h2 className="font-heading text-lg tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h2>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3">
        {listings.map((l) => (
          <MiniProductCard key={`rail-${title}-${l.id}`} listing={l} isGuest={isGuest} />
        ))}
      </div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeImage, setActiveImage] = useState(0);

  const [related, setRelated] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  const [orderTarget, setOrderTarget] = useState(null);

  useEffect(() => {
    const storedUser = getStoredUser();
    setUser(storedUser);
    setAuthChecked(true);
  }, []);

  const loadListing = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    listingsApi
      .get(id)
      .then((data) => {
        if (cancelled) return;
        setListing(data.listing);
        setActiveImage(0);
        addRecentlyViewed(data.listing.id);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Could not load this listing.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    const cleanup = loadListing();
    return cleanup;
  }, [loadListing]);

  // Recommended products — same category, server-picked.
  useEffect(() => {
    let cancelled = false;
    listingsApi
      .related(id)
      .then((data) => {
        if (!cancelled) setRelated(data.listings || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Recently viewed — resolved from localStorage ids, excluding the current listing.
  useEffect(() => {
    const ids = getRecentlyViewedExcluding(id);
    if (ids.length === 0) {
      setRecentlyViewed([]);
      return;
    }
    let cancelled = false;
    listingsApi
      .byIds(ids)
      .then((data) => {
        if (!cancelled) setRecentlyViewed(data.listings || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, listing]);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  const handleOrderClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role === 'buyer') setOrderTarget(listing);
  };

  const handleOrderSuccess = (qty) => {
    if (!orderTarget) return;
    toast.success(`Payment confirmed! ${qty} × ${orderTarget.title.slice(0, 30)}... sent to the seller.`);
    setListing((prev) => (prev ? { ...prev, quantity: prev.quantity - qty } : prev));
    setOrderTarget(null);
  };

  const isGuest = !user;
  const canOrder = user?.role === 'buyer';
  const images = listing ? [listing.imageUrl, ...(listing.galleryUrls || [])].filter(Boolean) : [];

  if (!authChecked || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-3" style={{ borderColor: 'var(--primary)' }} />
          <p className="text-sm font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>LOADING PRODUCT...</p>
        </div>
      </div>
    );
  }

  if (loadError || !listing) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
        <AppHeader userName={user?.name} userRole={user?.role} onLogout={handleLogout} currentPath="/marketplace" />
        <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 py-16 flex flex-col items-center justify-center">
          <AlertCircle size={40} className="mb-4" style={{ color: 'var(--danger)' }} />
          <h1 className="font-heading text-xl tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            LISTING NOT FOUND
          </h1>
          <p className="text-sm text-center max-w-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
            {loadError || 'This listing may have been removed or is no longer active.'}
          </p>
          <Link to="/marketplace" className="btn-primary px-4 py-2 text-sm">BACK TO MARKETPLACE</Link>
        </main>
        <AppFooter />
      </div>
    );
  }

  const specEntries = Object.entries(listing.specifications || {});

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
      <AppHeader userName={user?.name} userRole={user?.role} onLogout={handleLogout} currentPath="/marketplace" />

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
        {/* Breadcrumb */}
        <button
          onClick={() => navigate('/marketplace')}
          className="flex items-center gap-1.5 text-xs font-heading tracking-stamp mb-4 hover:opacity-70 transition-opacity"
          style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}
        >
          <ArrowLeft size={13} />
          BACK TO MARKETPLACE
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* ── Gallery ─────────────────────────────────────── */}
          <div className="lg:col-span-2">
            <div
              className="relative w-full border-2 border-foreground overflow-hidden"
              style={{ height: '340px', backgroundColor: 'var(--card)' }}
            >
              {listing.isFeatured && (
                <div
                  className="absolute top-0 left-0 z-10 flex items-center gap-1 px-2 py-1 text-xs font-heading tracking-stamp"
                  style={{ backgroundColor: 'var(--amber)', color: 'white', fontFamily: 'var(--font-heading)' }}
                >
                  <Star size={10} fill="white" />
                  FEATURED LISTING
                </div>
              )}
              {images.length > 0 ? (
                <img src={images[activeImage]} alt={listing.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                  <ImageOff size={32} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-xs font-heading tracking-stamp" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
                    NO IMAGE PROVIDED
                  </span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                {images.map((src, i) => (
                  <button
                    key={`thumb-${i}`}
                    onClick={() => setActiveImage(i)}
                    className="flex-shrink-0 border-2 overflow-hidden"
                    style={{
                      width: '64px',
                      height: '64px',
                      borderColor: i === activeImage ? 'var(--primary)' : 'var(--border)',
                    }}
                  >
                    <img src={src} alt={`${listing.title} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Seller Card */}
            <div className="mt-5 p-4 paper-texture border-2 border-foreground">
              <p className="text-xs font-heading tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                SOLD BY
              </p>
              <div className="flex items-start gap-2 mb-1">
                <Package size={16} style={{ color: 'var(--primary)', marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium">{listing.sellerCompany}</p>
                    {listing.sellerVerified && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs border"
                        style={{ borderColor: 'var(--secondary)', color: 'var(--secondary)', backgroundColor: 'var(--success-bg)' }}
                        title="Verified by ClearanceConnect"
                      >
                        <ShieldCheck size={10} />
                        VERIFIED
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{listing.sellerCity}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
                {listing.sellerVerified
                  ? 'This seller has completed ClearanceConnect verification for identity and business legitimacy.'
                  : 'Verification pending — order protection is still provided via escrow-style settlement.'}
              </p>
            </div>
          </div>

          {/* ── Details ─────────────────────────────────────── */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span
                className="text-xs font-heading tracking-stamp px-2 py-1 border"
                style={{ borderColor: 'var(--primary)', color: 'var(--primary)', fontFamily: 'var(--font-heading)' }}
              >
                {listing.category.toUpperCase()}
              </span>
              <div className="flex items-center gap-2">
                <StatusBadge status={listing.status} size="sm" />
                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  <Eye size={12} />
                  {listing.viewsCount?.toLocaleString('en-IN') ?? 0} views
                </span>
              </div>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl tracking-stamp leading-tight mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              {listing.title}
            </h1>

            <div className="flex flex-wrap gap-2 mb-4">
              {listing.brand && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs border" style={{ borderColor: 'var(--border)' }}>
                  <Tag size={11} /> {listing.brand}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs border" style={{ borderColor: 'var(--border)' }}>
                <BadgeCheck size={11} /> {listing.condition}
              </span>
              {listing.warranty && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs border" style={{ borderColor: 'var(--border)' }}>
                  <ShieldCheck size={11} /> {listing.warranty}
                </span>
              )}
            </div>

            {/* Price / Qty / MOQ */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="p-3 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                    UNIT PRICE
                  </p>
                  {listing.discountPercent > 0 && (
                    <span
                      className="text-xs font-heading tracking-stamp px-1.5 py-0.5"
                      style={{ backgroundColor: 'var(--secondary)', color: 'white', fontFamily: 'var(--font-heading)', fontSize: '10px' }}
                    >
                      {listing.discountPercent}% OFF
                    </span>
                  )}
                </div>
                {isGuest ? (
                  <div className="flex items-center gap-1">
                    <p className="text-lg font-heading select-none" style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary)', filter: 'blur(5px)', userSelect: 'none' }}>
                      {formatINR(listing.price)}
                    </p>
                    <Lock size={12} style={{ color: 'var(--amber)' }} />
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <p className="text-lg font-heading" style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}>
                      {formatINR(listing.price)}
                    </p>
                    {listing.marketValue > listing.price && (
                      <p className="text-xs line-through" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                        {formatINR(listing.marketValue)}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="p-3 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                  QTY AVAILABLE
                </p>
                <p className="text-lg font-heading" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                  {listing.quantity.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="p-3 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                  MIN. ORDER QTY
                </p>
                <p className="text-lg font-heading" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                  {listing.moq?.toLocaleString('en-IN') ?? 1}
                </p>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleOrderClick}
              disabled={!canOrder && !isGuest}
              className={`w-full md:w-auto px-8 py-3 text-sm flex items-center justify-center gap-2 mb-6 ${
                canOrder ? 'btn-primary' : isGuest ? 'btn-secondary' : 'border-2 border-border text-muted-foreground cursor-not-allowed'
              }`}
              style={{ fontFamily: 'var(--font-heading)', letterSpacing: '0.05em' }}
            >
              {isGuest ? <Lock size={15} /> : <ShoppingCart size={15} />}
              {canOrder ? 'PLACE ORDER' : isGuest ? 'LOG IN TO VIEW PRICE & ORDER' : 'BUYER LOGIN REQUIRED TO ORDER'}
            </button>

            {/* Full Description — seller-authored */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList size={15} style={{ color: 'var(--primary)' }} />
                <h2 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                  PRODUCT DESCRIPTION
                </h2>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--foreground)' }}>
                {listing.description}
              </p>
            </div>

            {/* Specifications table — seller-entered key/value pairs */}
            {specEntries.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Boxes size={15} style={{ color: 'var(--primary)' }} />
                  <h2 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                    SPECIFICATIONS
                  </h2>
                </div>
                <div className="border" style={{ borderColor: 'var(--border)' }}>
                  {specEntries.map(([key, value], i) => (
                    <div
                      key={`spec-${key}`}
                      className="grid grid-cols-2 text-sm"
                      style={{ backgroundColor: i % 2 === 0 ? 'var(--card)' : 'transparent', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}
                    >
                      <div className="px-3 py-2 font-medium" style={{ borderRight: '1px solid var(--border)' }}>{key}</div>
                      <div className="px-3 py-2" style={{ color: 'var(--muted-foreground)' }}>{String(value)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logistics / HSN footnote — ties to platform's logistics support */}
            <div className="p-3 border flex items-start gap-2" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--steel-bg)' }}>
              <Truck size={15} style={{ color: 'var(--steel)', marginTop: '1px', flexShrink: 0 }} />
              <p className="text-xs" style={{ color: 'var(--foreground)' }}>
                Pickup and delivery coordinated through ClearanceConnect's partnered logistics network. Payment is
                held in escrow until fulfillment is confirmed.
                {listing.hsnCode && <> HSN Code: <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{listing.hsnCode}</span>.</>}
              </p>
            </div>
          </div>
        </div>

        {/* ── Recommended Products ─────────────────────────── */}
        <ProductRail title="RECOMMENDED PRODUCTS" icon={Star} listings={related} isGuest={isGuest} />

        {/* ── Recently Viewed ──────────────────────────────── */}
        <ProductRail
          title="RECENTLY VIEWED"
          icon={Clock}
          listings={recentlyViewed}
          isGuest={isGuest}
          emptyMessage={null}
        />
      </main>

      <AppFooter />

      {orderTarget && (
        <OrderModal listing={orderTarget} onClose={() => setOrderTarget(null)} onSuccess={handleOrderSuccess} />
      )}

      <Toaster position="bottom-right" richColors />
    </div>
  );
}
